import { Transition } from '@headlessui/react'
import {
  PencilSquareIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/20/solid'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import Link from 'next/link'
import { LinkButton } from '@components/shared/Button'
import { useRouter } from 'next/router'
import ImageWithFallback from '@components/image/ImageWithFallback'
import store from 'stores/store'
import { TOKEN_WATCHLIST_KEY } from '@components/watchlist/WatchlistButton'
import { useRunOnceEffect } from 'hooks/useRunOnceEffect'
import { fetchMetadataForMints } from 'utils/tokens'
import { TokenMinimalData } from 'types/token'
import { useWatchlist } from 'hooks/useWatchlist'
import { formatNumericValue } from 'utils/numbers'
import SheenLoader from '@components/loading/SheenLoader'
import { Fragment, useState } from 'react'
import NumberFlash from '@components/shared/NumberFlash'
import EditWatchlistModal from '@components/modals/EditWatchlistModal'
import { useTranslation } from 'next-i18next'

const WatchlistBar = () => {
  const { t } = useTranslation('watchlist')
  const { asPath } = useRouter()
  const { watchlist, watchlistPricesLoading } = useWatchlist()
  const [, setWatchlist] = useLocalStorage(TOKEN_WATCHLIST_KEY)
  const setMarketAndSwapToken = store((s) => s.actions.setMarketAndSwapToken)
  const [showEditWatchlistModal, setShowEditWatchlistModal] = useState(false)

  useRunOnceEffect(
    'update-watchlist',
    watchlist,
    async () => {
      if (!watchlist.length) {
        return []
      }
      return (
        await fetchMetadataForMints(
          watchlist.map(({ mint }) => mint),
          undefined,
          store.getState().connection,
        )
      ).map(
        ({ mint, name, image_url, symbol }): TokenMinimalData => ({
          mint,
          name,
          image_url,
          symbol,
        }),
      )
    },
    (updatedTokens) =>
      setWatchlist(
        watchlist.map(
          (token) => updatedTokens.find((t) => t.mint === token.mint) ?? token,
        ),
      ),
  )

  return (
    <>
      <Transition
        as={Fragment}
        show={!!watchlist.length}
        enter="transition-all ease-in duration-200"
        enterFrom="opacity-0 max-h-0"
        enterTo="opacity-100 max-h-8"
        leave="transition-all ease-out duration-200"
        leaveFrom="opacity-100 max-h-8"
        leaveTo="opacity-0 max-h-0"
      >
        <div className="hide-scroll flex h-8 items-center justify-between overflow-x-auto border-b border-th-bkg-3 px-6">
          <div className="flex space-x-4">
            {watchlist.map((token) => {
              const { mint, image_url, symbol, price } = token ?? {}
              return mint ? (
                <div className="w-max" key={`${mint}${symbol}`}>
                  <Link
                    className="flex items-center text-th-fgd-3 md:hover:text-th-active"
                    href={`/token/${mint}`}
                    onClick={() => setMarketAndSwapToken(mint)}
                  >
                    {image_url ? (
                      <ImageWithFallback
                        className="mr-1.5"
                        height={16}
                        width={16}
                        src={image_url}
                        alt={`${symbol ?? ''} token logo`}
                      />
                    ) : (
                      <QuestionMarkCircleIcon className="mr-1.5 size-4 text-th-fgd-4" />
                    )}
                    <span
                      className={`mr-1.5 text-xs ${
                        asPath.includes(mint) ? 'text-th-active' : ''
                      }`}
                    >
                      {symbol || '???'}
                    </span>
                    {price ? (
                      <NumberFlash
                        className="text-xs text-th-fgd-1"
                        number={formatNumericValue(price)}
                        prefix="$"
                      />
                    ) : watchlistPricesLoading ? (
                      <SheenLoader>
                        <div className="h-4 w-12 bg-th-bkg-2" />
                      </SheenLoader>
                    ) : null}
                  </Link>
                </div>
              ) : null
            })}
          </div>
          <LinkButton
            className="ml-4 flex items-center gap-1.5"
            onClick={() => setShowEditWatchlistModal(true)}
          >
            <PencilSquareIcon className="size-3.5" />
            <span className="text-xs font-bold">{t('edit')}</span>
          </LinkButton>
        </div>
      </Transition>
      {showEditWatchlistModal ? (
        <EditWatchlistModal
          isOpen={showEditWatchlistModal}
          onClose={() => setShowEditWatchlistModal(false)}
        />
      ) : null}
    </>
  )
}

export default WatchlistBar
